import { Box, Typography, Chip, Divider, SvgIcon, Button, CircularProgress, Alert } from '@mui/material';
import { FC, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useStyles } from '../../../theme/styles.helpers';
import { Close } from '@mui/icons-material';
import { BookingDetailsStyles } from './BookingDetailsStyles';
import { SesamiBooking } from '../../../pages/calendar/Calendar';
import { AuthContext } from '../../../aws/AuthProvider';
import { cancelSesamiAppointment, noShowSesamiAppointment, checkInSesamiAppointment } from '../../../aws/RequestsApi';
import AWSAppSyncClient from 'aws-appsync';
import { NormalizedCacheObject } from '../../../aws/aws.config';

interface BookingDetailsModalProps {
  onModalClose: () => void;
  onBookingUpdate?: () => void;
  bookingData: SesamiBooking;
}

export const BookingDetails: FC<BookingDetailsModalProps> = ({ onModalClose, onBookingUpdate, bookingData }) => {
  const { t } = useTranslation();
  const styles = useStyles(BookingDetailsStyles, {});
  const { apiClient } = useContext(AuthContext);

  const [loading, setLoading] = useState<{ [key: string]: boolean }>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDateTime = (dateTimeString: string) => {
    const date = new Date(dateTimeString);
    return date.toLocaleString();
  };

  const handleAction = async (
    action: string,
    actionFunction: (appointmentId: string, apiClient: AWSAppSyncClient<NormalizedCacheObject>) => Promise<boolean>
  ) => {
    if (!apiClient) {
      setError(t('API client not available'));
      return;
    }

    setLoading((prev) => ({ ...prev, [action]: true }));
    setError(null);
    setSuccess(null);

    try {
      const result = await actionFunction(bookingData.id, apiClient);
      if (result) {
        setSuccess(t(`${action} successful`));
        if (onBookingUpdate) {
          onBookingUpdate();
        }
      } else {
        setError(t(`${action} failed`));
      }
    } catch (err) {
      console.error(`Error ${action}:`, err);
      setError(t(`${action} failed: ${err instanceof Error ? err.message : 'Unknown error'}`));
    } finally {
      setLoading((prev) => ({ ...prev, [action]: false }));
    }
  };

  const handleCancel = () => handleAction('cancel', cancelSesamiAppointment);
  const handleNoShow = () => handleAction('no-show', noShowSesamiAppointment);
  const handleCheckIn = () => handleAction('check-in', checkInSesamiAppointment);

  const canPerformAction = (action: string) => {
    const currentStatus = bookingData.status.toLowerCase();
    switch (action) {
      case 'cancel':
        return currentStatus === 'confirmed' || currentStatus === 'pending';
      case 'no-show':
        return currentStatus === 'confirmed';
      case 'check-in':
        return currentStatus === 'confirmed';
      default:
        return false;
    }
  };

  return (
    <Box sx={styles.root} className="BookingDetailsModal-root">
      <Box sx={styles.container}>
        <Box sx={styles.header}>
          <Box sx={styles.headerTitle}>
            <Typography variant="h4">{t('Booking Details')}</Typography>
          </Box>
        </Box>

        <Box sx={styles.bookingDetails} className="BookingDetailsModal-grid">
          {/* Left side - Customer Information */}
          <Box sx={styles.leftContainer}>
            <Typography variant="h4">{t('Customer Information')}</Typography>
            <Divider sx={styles.divider} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {t('Name')}
              </Typography>
              <Typography variant="body1" sx={styles.value}>
                {bookingData.customerName}
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {t('Email')}
              </Typography>
              <Typography variant="body1" sx={styles.value}>
                {bookingData.customerEmail}
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {t('Phone')}
              </Typography>
              <Typography variant="body1" sx={styles.value}>
                {bookingData.customerPhone || t('Not provided')}
              </Typography>
            </Box>

          </Box>

          {/* Right side - Service and Appointment Details */}
          <Box sx={styles.rightContainer}>
            <Box>
              <Typography variant="h4">{t('Service Information')}</Typography>
              <Divider sx={styles.divider} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('Service')}
                </Typography>
                <Typography variant="body1" sx={styles.value}>
                  {bookingData.serviceName}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('Variant')}
                </Typography>
                <Typography variant="body1" sx={styles.value}>
                  {bookingData.variantTitle}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('Duration')}
                </Typography>
                <Typography variant="body1" sx={styles.value}>
                  {bookingData.duration} {t('minutes')}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('Price')}
                  </Typography>
                  <Typography variant="body1" sx={styles.value}>
                    {bookingData.price} {bookingData.currency}
                  </Typography>
                </Box>
                <Chip
                  label={t(bookingData.status)}
                  color={getStatusColor(bookingData.status)}
                  size="small"
                  sx={styles.statusChip}
                />
              </Box>

              <Typography variant="h4">{t('Appointment Details')}</Typography>
              <Divider sx={styles.divider} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('Start Time')}
                </Typography>
                <Typography variant="body1" sx={styles.value}>
                  {formatDateTime(bookingData.datetime)}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('End Time')}
                </Typography>
                <Typography variant="body1" sx={styles.value}>
                  {formatDateTime(new Date(new Date(bookingData.datetime).getTime() + bookingData.duration * 60000).toISOString())}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('Team Member')}
                </Typography>
                <Typography variant="body1" sx={styles.value}>
                  {bookingData.teamMember || t('Not assigned')}
                </Typography>
              </Box>

              {/* Action Buttons */}
              <Typography variant="h4">{t('Actions')}</Typography>
              <Divider sx={styles.divider} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                {error && (
                  <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                {success && (
                  <Alert severity="success" onClose={() => setSuccess(null)}>
                    {success}
                  </Alert>
                )}

                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={handleCancel}
                  disabled={!canPerformAction('cancel') || loading.cancel}
                  startIcon={loading.cancel ? <CircularProgress size={20} /> : null}
                >
                  {loading.cancel ? t('Cancelling...') : t('Cancel Appointment')}
                </Button>

                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  onClick={handleNoShow}
                  disabled={!canPerformAction('no-show') || loading['no-show']}
                  startIcon={loading['no-show'] ? <CircularProgress size={20} /> : null}
                >
                  {loading['no-show'] ? t('Marking as No-Show...') : t('Mark as No-Show')}
                </Button>

                <Button
                  variant="outlined"
                  color="success"
                  size="small"
                  onClick={handleCheckIn}
                  disabled={!canPerformAction('check-in') || loading['check-in']}
                  startIcon={loading['check-in'] ? <CircularProgress size={20} /> : null}
                >
                  {loading['check-in'] ? t('Checking In...') : t('Check In')}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Closing Icon */}
      <Box onClick={onModalClose} sx={{ marginLeft: 2, cursor: 'pointer' }}>
        <SvgIcon sx={{ color: 'primary.main' }} component={Close}></SvgIcon>
      </Box>
    </Box>
  );
};
